import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { nutrition } from '../../lib/api';
import { toast } from 'sonner';
import {
  Apple,
  Beef,
  ChevronDown,
  CircleGauge,
  Droplets,
  Flame,
  Leaf,
  Plus,
  RefreshCcw,
  Salad,
  Sparkles,
  Trash2,
  Utensils,
  Wheat,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import i18nInstance from '../../i18n';


const t = (key, fallback, options = {}) =>
  i18nInstance.t(key, typeof fallback === 'string' ? { defaultValue: fallback, ...options } : fallback);
const PERIODS = ['training', 'tournament', 'bulking', 'recovery'];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const MEAL_TIMES = ['breakfast', 'lunch', 'dinner', 'snack'];
const SUPPLEMENTS = ['electrolytes', 'protein', 'magnesium', 'omega3'];

const emptyMealForm = () => ({
  period: 'training',
  day_of_week: 'monday',
  meal_time: 'breakfast',
  dishes: '',
  calories: '',
  protein: '',
  fats: '',
  carbs: '',
  comment: '',
});

const sumMacro = (plans, key) =>
  Math.round(plans.reduce((sum, plan) => sum + (Number(plan[key]) || 0), 0) * 10) / 10;

const mealMeta = (mealTime) => {
  if (mealTime === 'breakfast') {
    return { icon: <Apple size={15} strokeWidth={1.8} />, tone: 'bg-amber-50 text-amber-700 ring-amber-100' };
  }
  if (mealTime === 'lunch') {
    return { icon: <Utensils size={15} strokeWidth={1.8} />, tone: 'bg-lime-50 text-lime-700 ring-lime-100' };
  }
  if (mealTime === 'dinner') {
    return { icon: <Salad size={15} strokeWidth={1.8} />, tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100' };
  }
  return { icon: <Leaf size={15} strokeWidth={1.8} />, tone: 'bg-zinc-100 text-zinc-700 ring-zinc-200' };
};

const periodLabel = (period, translate) => translate(`nutrition.periods.${period}`);
const dayLabel = (day, translate) => translate(`nutrition.days.${day}`);
const mealTimeLabel = (mealTime, translate) => translate(`nutrition.mealTimes.${mealTime}`);
const localeFor = (lang) => (lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-GB');

const formatKcal = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.round(number);
};

const formatEnergyDate = (value, lang) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(localeFor(lang), {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const equipmentTypeLabel = (equipmentType, translate) => {
  if (!equipmentType) return translate('nutrition.completedWorkout');
  const fallback = String(equipmentType).replace(/_/g, ' ');
  return translate(`nutrition.equipmentTypes.${equipmentType}`, fallback);
};

const NutritionSkeleton = () => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="nutrition-loading">
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="h-56 animate-pulse rounded-lg bg-zinc-200" />
      <div className="grid gap-4 md:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="h-32 animate-pulse rounded-lg bg-zinc-200" />
        ))}
      </div>
      <div className="h-96 animate-pulse rounded-lg bg-zinc-200" />
    </div>
  </div>
);

const NutritionErrorState = ({ onRetry, t }) => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="nutrition-page">
    <div className="mx-auto max-w-7xl">
      <section className="rounded-lg border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-100">
          <RefreshCcw size={24} strokeWidth={1.8} />
        </div>
        <h1 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
          {t('nutrition.loadError')}
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
          {t('nutrition.loadErrorBody')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
        >
          <RefreshCcw size={16} strokeWidth={1.8} />
          {t('common.retry')}
        </button>
      </section>
    </div>
  </div>
);

const MealTypeBadge = ({ mealTime, t }) => {
  const meta = mealMeta(mealTime);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black ring-1 ${meta.tone}`}>
      {meta.icon}
      {mealTimeLabel(mealTime, t)}
    </span>
  );
};

const MacroSummaryCard = ({ icon, label, value, unit, tone, testId }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" data-testid={testId}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-zinc-500">{label}</p>
        <p className="mt-2 text-3xl font-black tracking-normal text-zinc-950">{value}</p>
        <p className="mt-1 text-sm text-zinc-500">{unit}</p>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-lg ring-1 ${tone}`}>
        {icon}
      </div>
    </div>
  </div>
);

const EnergyBalanceCard = ({ summary, t }) => {
  const consumed = formatKcal(summary?.consumed_calories);
  const burned = formatKcal(summary?.burned_calories);
  const net = formatKcal(summary?.net_calories);

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm" data-testid="nutrition-energy-balance">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-700 ring-1 ring-orange-100">
          <CircleGauge size={22} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('nutrition.energyBalance')}</p>
          <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{t('nutrition.energyBalanceTitle')}</h2>
          <p className="mt-2 text-sm leading-6 text-zinc-500">{t('nutrition.energyBalanceBody')}</p>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <MacroPill label={t('nutrition.consumedCalories')} value={consumed} tone="bg-orange-50 text-orange-700 ring-orange-100" />
        <MacroPill label={t('nutrition.burnedCalories')} value={burned} tone="bg-rose-50 text-rose-700 ring-rose-100" />
        <MacroPill label={t('nutrition.netCalories')} value={net} tone="bg-zinc-100 text-zinc-800 ring-zinc-200" />
      </div>
    </section>
  );
};

const NutritionHeroCard = ({ plans, macros, selectedPeriod, onAdd, t }) => (
  <section className="relative overflow-hidden rounded-lg bg-zinc-950 p-5 text-white shadow-sm md:p-7">
    <div className="absolute inset-x-0 top-0 h-px bg-lime-300" />
    <div className="absolute right-0 top-0 h-44 w-44 rounded-full bg-lime-300 opacity-10 blur-3xl" />
    <div className="relative grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-lime-200">
          <Salad size={14} strokeWidth={1.8} />
          {t('nutrition.heroKicker')}
        </div>
        <h1 className="mt-4 text-3xl font-black tracking-normal text-white md:text-4xl">
          {t('nutrition.title')}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
          {t('nutrition.subtitle')}
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
          data-testid="add-meal-button"
        >
          <Plus size={16} strokeWidth={1.8} />
          {t('nutrition.addMeal')}
        </button>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/10 p-4">
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
          {periodLabel(selectedPeriod, t)}
        </p>
        <div className="mt-4 flex items-end justify-between gap-4">
          <div>
            <p className="text-5xl font-black tracking-normal text-white">{plans.length}</p>
            <p className="mt-1 text-sm text-zinc-300">{t('nutrition.mealsInPeriod')}</p>
          </div>
          <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-lime-300 text-zinc-950">
            <Utensils size={30} strokeWidth={1.8} />
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-lg font-black text-white">{macros.calories}</p>
            <p className="mt-1 text-xs text-zinc-400">kcal</p>
          </div>
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-lg font-black text-white">{macros.protein}</p>
            <p className="mt-1 text-xs text-zinc-400">{t('nutrition.proteinShort')}</p>
          </div>
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-lg font-black text-white">{macros.carbs}</p>
            <p className="mt-1 text-xs text-zinc-400">{t('nutrition.carbsShort')}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const NutritionPeriodSelector = ({ selectedPeriod, setSelectedPeriod, t }) => (
  <div className="flex gap-2 overflow-x-auto rounded-lg bg-zinc-100 p-1">
    {PERIODS.map((period) => (
      <button
        key={period}
        type="button"
        onClick={() => setSelectedPeriod(period)}
        data-testid={`period-${period}`}
        className={`min-h-11 rounded-md px-4 text-sm font-black transition ${
          selectedPeriod === period
            ? 'bg-white text-zinc-950 shadow-sm'
            : 'text-zinc-500 hover:text-zinc-900'
        }`}
      >
        {periodLabel(period, t)}
      </button>
    ))}
  </div>
);

const MealCard = ({ plan, onDelete, t }) => (
  <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <MealTypeBadge mealTime={plan.meal_time} t={t} />
          {plan.calories ? (
            <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black text-orange-700 ring-1 ring-orange-100">
              {plan.calories} kcal
            </span>
          ) : null}
        </div>
        <h3 className="mt-3 font-black leading-6 text-zinc-950">{plan.dishes}</h3>
        {plan.comment ? <p className="mt-2 text-sm leading-5 text-zinc-500">{plan.comment}</p> : null}
      </div>
      <button
        type="button"
        onClick={() => onDelete(plan.id)}
        className="inline-flex min-h-11 w-11 shrink-0 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-rose-50 hover:text-rose-700"
        data-testid={`delete-meal-${plan.id}`}
        aria-label={t('nutrition.deleteMeal')}
      >
        <Trash2 size={18} strokeWidth={1.8} />
      </button>
    </div>
    {(plan.protein || plan.fats || plan.carbs) ? (
      <div className="mt-4 grid grid-cols-3 gap-2">
        <MacroPill label={t('nutrition.proteinShort')} value={plan.protein} tone="bg-blue-50 text-blue-700 ring-blue-100" />
        <MacroPill label={t('nutrition.carbsShort')} value={plan.carbs} tone="bg-purple-50 text-purple-700 ring-purple-100" />
        <MacroPill label={t('nutrition.fatsShort')} value={plan.fats} tone="bg-emerald-50 text-emerald-700 ring-emerald-100" />
      </div>
    ) : null}
  </article>
);

const MacroPill = ({ label, value, tone }) => (
  <div className={`rounded-lg p-3 text-center ring-1 ${tone}`}>
    <p className="text-lg font-black">{value ?? '-'}</p>
    <p className="mt-1 text-xs font-black uppercase tracking-[0.08em]">{label}</p>
  </div>
);

const NutritionEmptyState = ({ onAdd, t }) => (
  <section className="rounded-lg border border-dashed border-zinc-300 bg-white p-8 text-center">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
      <Salad size={26} strokeWidth={1.8} />
    </div>
    <h2 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
      {t('common.noData')}
    </h2>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
      {t('nutrition.emptyBody')}
    </p>
    <button
      type="button"
      onClick={onAdd}
      className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
    >
      <Plus size={16} strokeWidth={1.8} />
      {t('nutrition.addMeal')}
    </button>
  </section>
);

const HydrationCard = () => (
  <section className="rounded-lg border border-blue-200 bg-blue-50 p-5 text-blue-900 shadow-sm">
    <div className="flex items-start gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white text-blue-700 ring-1 ring-blue-100">
        <Droplets size={22} strokeWidth={1.8} />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-700">{t('nutrition.hydration')}</p>
        <h2 className="mt-1 text-xl font-black tracking-normal">{t('nutrition.manualNote')}</h2>
        <p className="mt-2 text-sm leading-6 opacity-85">
          {t('nutrition.hydrationUnsupported')}
        </p>
      </div>
    </div>
  </section>
);

const AIRecommendationCard = () => (
  <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
    <div className="flex items-start gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
        <Sparkles size={22} strokeWidth={1.8} />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('nutrition.aiNutrition')}</p>
        <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{t('nutrition.notEnabled')}</h2>
        <p className="mt-2 text-sm leading-6 text-zinc-500">
          {t('nutrition.aiUnsupported')}
        </p>
      </div>
    </div>
  </section>
);

const RecoveryNutritionCard = ({ recoveryPlans }) => (
  <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-emerald-950 shadow-sm">
    <div className="flex items-start gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white text-emerald-700 ring-1 ring-emerald-100">
        <Leaf size={22} strokeWidth={1.8} />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">{t('nutrition.recoveryNutrition')}</p>
        <h2 className="mt-1 text-xl font-black tracking-normal">{t('nutrition.recoveryMeals', { count: recoveryPlans.length })}</h2>
        <p className="mt-2 text-sm leading-6 opacity-85">
          {t('nutrition.recoverySupportBody')}
        </p>
      </div>
    </div>
  </section>
);

const EnergyExpenditureCard = ({ expenditures, lang, t }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm" data-testid="nutrition-energy-expenditures">
    <div className="flex items-start gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-100">
        <Flame size={22} strokeWidth={1.8} />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('nutrition.energyExpenditure')}</p>
        <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{t('nutrition.workoutCaloriesBurned')}</h2>
      </div>
    </div>

    {expenditures.length ? (
      <div className="mt-4 space-y-3">
        {expenditures.slice(0, 6).map((entry) => {
          const explanation = entry.explanation || t('nutrition.energyExpenditureFallback');
          return (
            <article key={entry.id} className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-black text-zinc-950">{equipmentTypeLabel(entry.equipment_type, t)}</p>
                  <p className="mt-1 text-xs font-semibold text-zinc-500">
                    {formatEnergyDate(entry.recorded_at, lang) || t('common.date')}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-rose-50 px-3 py-1 text-xs font-black text-rose-700 ring-1 ring-rose-100">
                  {formatKcal(entry.calories_burned)} kcal
                </span>
              </div>
              <details className="mt-3 text-sm leading-6 text-zinc-600">
                <summary className="cursor-pointer font-bold text-zinc-700">{t('nutrition.energyExplanation')}</summary>
                <p className="mt-2">{explanation}</p>
              </details>
            </article>
          );
        })}
      </div>
    ) : (
      <p className="mt-4 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
        {t('nutrition.energyExpenditureEmpty')}
      </p>
    )}
  </section>
);

const AddMealModal = ({ formData, setFormData, onSubmit, onClose, t }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-zinc-950/60 p-4"
    data-testid="add-meal-modal"
    role="dialog"
    aria-modal="true"
  >
    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('nutrition.addMeal')}</p>
          <h2 className="mt-1 text-2xl font-black tracking-normal text-zinc-950">{t('nutrition.title')}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex min-h-11 w-11 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900"
          aria-label={t('common.cancel')}
        >
          <X size={20} strokeWidth={1.8} />
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <SelectField label={t('nutrition.nutritionPeriod')} value={formData.period} onChange={(value) => setFormData({ ...formData, period: value })}>
          {PERIODS.map((period) => <option key={period} value={period}>{periodLabel(period, t)}</option>)}
        </SelectField>
        <SelectField label={t('common.date')} value={formData.day_of_week} onChange={(value) => setFormData({ ...formData, day_of_week: value })}>
          {DAYS.map((day) => <option key={day} value={day}>{dayLabel(day, t)}</option>)}
        </SelectField>
        <SelectField label={t('nutrition.mealTime')} value={formData.meal_time} onChange={(value) => setFormData({ ...formData, meal_time: value })}>
          {MEAL_TIMES.map((meal) => <option key={meal} value={meal}>{mealTimeLabel(meal, t)}</option>)}
        </SelectField>

        <label className="block">
          <span className="mb-2 block text-sm font-bold text-zinc-700">{t('nutrition.dishes')}</span>
          <textarea
            className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            rows={3}
            value={formData.dishes}
            onChange={(event) => setFormData({ ...formData, dishes: event.target.value })}
            required
            data-testid="input-dishes"
          />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <NumberField label={t('nutrition.calories')} value={formData.calories} onChange={(value) => setFormData({ ...formData, calories: value })} />
          <NumberField label={t('nutrition.proteinGrams')} value={formData.protein} onChange={(value) => setFormData({ ...formData, protein: value })} step="0.1" />
          <NumberField label={t('nutrition.fatsGrams')} value={formData.fats} onChange={(value) => setFormData({ ...formData, fats: value })} step="0.1" />
          <NumberField label={t('nutrition.carbsGrams')} value={formData.carbs} onChange={(value) => setFormData({ ...formData, carbs: value })} step="0.1" />
        </div>

        <label className="block">
          <span className="mb-2 block text-sm font-bold text-zinc-700">{t('common.notes')}</span>
          <textarea
            className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            rows={2}
            value={formData.comment}
            onChange={(event) => setFormData({ ...formData, comment: event.target.value })}
          />
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
            data-testid="submit-meal"
          >
            {t('nutrition.addMeal')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-bold text-zinc-700">{label}</span>
    <div className="relative">
      <select
        className="min-h-11 w-full appearance-none rounded-lg border border-zinc-200 bg-white px-3 pr-9 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} strokeWidth={1.8} />
    </div>
  </label>
);

const NumberField = ({ label, value, onChange, step = '1' }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-bold text-zinc-700">{label}</span>
    <input
      type="number"
      step={step}
      className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

export const NutritionPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [energyExpenditures, setEnergyExpenditures] = useState([]);
  const [energySummary, setEnergySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('training');
  const [formData, setFormData] = useState(emptyMealForm);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [plansResponse, expendituresResponse, summaryResponse] = await Promise.all([
        nutrition.getAll(),
        nutrition.getEnergyExpendituresRecent({ limit: 20 }),
        nutrition.getSummary(),
      ]);
      setPlans(Array.isArray(plansResponse.data) ? plansResponse.data : []);
      setEnergyExpenditures(Array.isArray(expendituresResponse.data) ? expendituresResponse.data : []);
      setEnergySummary(summaryResponse.data || null);
    } catch (error) {
      toast.error(t('nutrition.loadError'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const resetForm = () => setFormData(emptyMealForm());

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await nutrition.create({
        ...formData,
        athlete_id: user?.id,
        calories: formData.calories ? parseInt(formData.calories, 10) : null,
        protein: formData.protein ? parseFloat(formData.protein) : null,
        fats: formData.fats ? parseFloat(formData.fats) : null,
        carbs: formData.carbs ? parseFloat(formData.carbs) : null,
      });
      toast.success(t('nutrition.addSuccess'));
      setShowModal(false);
      fetchPlans();
      resetForm();
    } catch (error) {
      toast.error(t('nutrition.addError'));
    }
  };

  const handleDelete = async (planId) => {
    try {
      await nutrition.delete(planId);
      toast.success(t('nutrition.deleteSuccess'));
      fetchPlans();
    } catch (error) {
      toast.error(t('nutrition.deleteError'));
    }
  };

  const filteredPlans = useMemo(
    () => plans.filter((plan) => plan.period === selectedPeriod),
    [plans, selectedPeriod],
  );

  const macros = useMemo(
    () => ({
      calories: sumMacro(filteredPlans, 'calories'),
      protein: sumMacro(filteredPlans, 'protein'),
      fats: sumMacro(filteredPlans, 'fats'),
      carbs: sumMacro(filteredPlans, 'carbs'),
    }),
    [filteredPlans],
  );

  const recoveryPlans = useMemo(
    () => plans.filter((plan) => plan.period === 'recovery'),
    [plans],
  );

  if (loading) return <NutritionSkeleton />;
  if (loadFailed) return <NutritionErrorState onRetry={fetchPlans} t={t} />;

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="nutrition-page">
      <div className="mx-auto max-w-7xl space-y-5">
        <NutritionHeroCard
          plans={filteredPlans}
          macros={macros}
          selectedPeriod={selectedPeriod}
          onAdd={() => setShowModal(true)}
          t={t}
        />

        <div className="grid gap-4 md:grid-cols-4">
          <MacroSummaryCard icon={<Flame size={22} />} label={t('nutrition.calories')} value={macros.calories} unit="kcal" tone="bg-orange-50 text-orange-700 ring-orange-100" />
          <MacroSummaryCard icon={<Beef size={22} />} label={t('nutrition.proteinShort')} value={macros.protein} unit="g" tone="bg-blue-50 text-blue-700 ring-blue-100" />
          <MacroSummaryCard icon={<Wheat size={22} />} label={t('nutrition.carbsShort')} value={macros.carbs} unit="g" tone="bg-purple-50 text-purple-700 ring-purple-100" />
          <MacroSummaryCard icon={<Leaf size={22} />} label={t('nutrition.fatsShort')} value={macros.fats} unit="g" tone="bg-emerald-50 text-emerald-700 ring-emerald-100" />
        </div>

        <EnergyBalanceCard summary={energySummary} t={t} />

        <div className="grid gap-5 xl:grid-cols-[1.25fr_0.75fr]">
          <div className="space-y-5">
            <NutritionPeriodSelector selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod} t={t} />

            {filteredPlans.length === 0 ? (
              <NutritionEmptyState onAdd={() => setShowModal(true)} t={t} />
            ) : (
              <div className="space-y-5">
                {DAYS.map((day) => {
                  const dayPlans = filteredPlans.filter((plan) => plan.day_of_week === day);
                  if (dayPlans.length === 0) return null;
                  return (
                    <section key={day} className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm" data-testid={`day-section-${day}`}>
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('nutrition.plan')}</p>
                          <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{dayLabel(day, t)}</h2>
                        </div>
                        <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-black text-zinc-700 ring-1 ring-zinc-200">
                          {dayPlans.length}
                        </span>
                      </div>
                      <div className="grid gap-3 lg:grid-cols-2">
                        {dayPlans.map((plan) => (
                          <MealCard key={plan.id} plan={plan} onDelete={handleDelete} t={t} />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <EnergyExpenditureCard expenditures={energyExpenditures} lang={i18n.language} t={t} />
            <HydrationCard />
            <RecoveryNutritionCard recoveryPlans={recoveryPlans} />
            <AIRecommendationCard />
            <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('nutrition.supplements')}</p>
              <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{t('nutrition.optionalNotes')}</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {SUPPLEMENTS.map((item) => (
                  <span key={item} className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-black text-zinc-700 ring-1 ring-zinc-200">
                    {t(`nutrition.supplementItems.${item}`)}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-sm leading-6 text-zinc-500">
                {t('nutrition.supplementUnsupported')}
              </p>
            </section>
          </aside>
        </div>
      </div>

      {showModal && (
        <AddMealModal
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onClose={() => setShowModal(false)}
          t={t}
        />
      )}
    </div>
  );
};
